import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import AirlineSeatLegroomExtraOutlinedIcon from '@mui/icons-material/AirlineSeatLegroomExtraOutlined';
import AlChip from '../../../common/components/AlChip';
import { currency } from '../../../common/utils/currency';
import { SERVICE_TYPE_ID, getServiceTypeById } from '../../models/service-type';

const LEGROOM_MAX = 3;

const AirportShuttlePlusUpsell = ({ service, totalPassengers, selected = false, onSelect, sx }) => {
    const { t } = useTranslation();

    const collectivePrice = parseFloat(service[getServiceTypeById(SERVICE_TYPE_ID.COLLECTIVE)]);

    const onClick = () => {
        if (selected) {
            return;
        }
        const maxQty = Math.min(totalPassengers, LEGROOM_MAX);
        onSelect(maxQty);
    };

    return (
        <Box
            sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                py: 2.5,
                px: 2.5,
                borderBottomLeftRadius: (theme) => theme.shape.borderRadius,
                borderBottomRightRadius: (theme) => theme.shape.borderRadius,
                ...sx,
            }}
            data-cy="airportShuttlePlusUpsell"
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
            >
                <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="h5">{t('booking.airportShuttlePlus.title')}</Typography>
                        <AlChip
                            color="info"
                            backgroundColor="#2EA3F21A"
                            label={t('booking.airportShuttlePlus.chip')}
                            icon={<AirlineSeatLegroomExtraOutlinedIcon />}
                        />
                    </Stack>
                    <Typography variant="body2" sx={{ mb: { xs: 1.5, sm: 0 } }}>
                        {t('booking.airportShuttlePlus.description')}
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 3 }}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    }}
                >
                    <Stack spacing={0} sx={{ alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                            <Typography variant="body2">{t('booking.airportShuttlePlus.pricePerSeat')}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                +{currency(collectivePrice)}
                            </Typography>
                        </Stack>
                        {selected && (
                            <Typography
                                variant="caption"
                                sx={{ opacity: 0.85 }}
                                data-cy="airportShuttlePlusUpsellIncludedNote"
                            >
                                {t('booking.airportShuttlePlus.includedInPrice')}
                            </Typography>
                        )}
                    </Stack>
                    <Button
                        variant={selected ? 'outlined' : 'contained'}
                        color={selected ? 'inherit' : 'secondary'}
                        size="large"
                        onClick={onClick}
                        startIcon={selected ? <CheckIcon /> : null}
                        data-cy="airportShuttlePlusUpsellSelect"
                        sx={{ flexShrink: 0 }}
                    >
                        {selected ? t('booking.airportShuttlePlus.selected') : t('select')}
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
};

export { AirportShuttlePlusUpsell };
